import { FC, KeyboardEvent, MouseEvent } from 'react';
import classNames from 'classnames';

import getClassNames from '../../utils/getClassNames';
import Icon from '../Icon';
import styles from './ChevronButtons.module.scss';

export type ChevronButtonsProps = {
  previousDisabled: boolean;
  nextDisabled: boolean;
  classNameModifier?: string;
  previous: (event?: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
  next: (event?: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
};

const captureKeyboardEvent = (fn: () => void, disabled: boolean) => (
  event: KeyboardEvent<HTMLButtonElement>,
) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) fn();
  }
};

const ChevronButton: FC<ChevronButtonsProps> = ({
  previousDisabled,
  nextDisabled,
  classNameModifier,
  previous,
  next,
}) => (
  <div className={getClassNames(styles, 'controls', classNameModifier)}>
    <button
      type="button"
      onClick={previous}
      onKeyDown={captureKeyboardEvent(previous, previousDisabled)}
      aria-hidden={previousDisabled}
      disabled={previousDisabled} // According to https://dequeuniversity.com/rules/axe/4.7/aria-hidden-focus we should utilize disabled prop to change element focus
      title="Back"
      aria-label="Back"
      className={classNames(
        getClassNames(styles, 'backButton', classNameModifier),
        getClassNames(styles, 'button', classNameModifier),
      )}
    >
      <Icon
        id="nav/chevron-left"
        className={getClassNames(styles, 'backButtonChevron', classNameModifier)}
      />
    </button>
    <button
      type="button"
      onClick={next}
      onKeyDown={captureKeyboardEvent(next, nextDisabled)}
      aria-hidden={nextDisabled}
      disabled={nextDisabled}
      title="Next"
      aria-label="Next"
      className={classNames(
        getClassNames(styles, 'nextButton', classNameModifier),
        getClassNames(styles, 'button', classNameModifier),
      )}
    >
      <Icon
        id="nav/chevron-right"
        className={getClassNames(styles, 'nextButtonChevron', classNameModifier)}
      />
    </button>
  </div>
);

export default ChevronButton;
